import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  BellIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  SparklesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

/**
 * SettingsPanel - Manage chat preferences and settings
 *
 * Features:
 * - Chat appearance preferences
 * - Notification settings
 * - Agent preferences (default agent, auto-switch)
 * - Conversation management (retention, export/import)
 * - Privacy settings
 */
const SettingsPanel = ({ user, company }) => {
  const intl = useIntl();
  // Load settings from localStorage
  const loadSettings = () => {
    const stored = localStorage.getItem('succession_chat_settings');
    return stored ? JSON.parse(stored) : {
      // Appearance
      theme: 'light',
      compactMode: false,

      // Notifications
      desktopNotifications: false,
      soundEnabled: true,
      unreadBadge: true,

      // Agent Preferences
      defaultAgent: 'succession_dashboard_agent',
      autoSwitchEnabled: true,
      showAgentSuggestions: true,

      // Conversation Management
      retentionDays: 30,
      autoDeleteOld: false,
      maxConversations: 50,

      // Privacy
      saveHistory: true,
      shareAnalytics: true
    };
  };

  const [settings, setSettings] = useState(loadSettings());
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', null

  // Save settings to localStorage whenever they change
  useEffect(() => {
    setSaveStatus('saving');
    const timer = setTimeout(() => {
      localStorage.setItem('succession_chat_settings', JSON.stringify(settings));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }, 500);

    return () => clearTimeout(timer);
  }, [settings]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleExportSettings = () => {
    const data = {
      settings,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-settings-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSettings = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.settings) {
          setSettings(data.settings);
          alert(intl.formatMessage({ id: 'succession.agent_system.settings_panel.imported_successfully' }));
        } else {
          alert(intl.formatMessage({ id: 'succession.agent_system.settings_panel.invalid_format' }));
        }
      } catch (error) {
        alert(intl.formatMessage({ id: 'succession.agent_system.settings_panel.error_reading' }));
      }
    };
    reader.readAsText(file);
  };

  const handleResetSettings = () => {
    if (confirm(intl.formatMessage({ id: 'succession.agent_system.settings_panel.confirm_reset' }))) {
      localStorage.removeItem('succession_chat_settings');
      setSettings(loadSettings());
    }
  };

  const handleClearHistory = () => {
    if (confirm(intl.formatMessage({ id: 'succession.agent_system.settings_panel.confirm_clear_history' }))) {
      localStorage.removeItem('succession_conversations');
      alert(intl.formatMessage({ id: 'succession.agent_system.settings_panel.history_cleared' }));
    }
  };

  // Agent options - labels come from intl for display; values are kept as-is
  const agentOptions = [
    { value: 'succession_dashboard_agent', label: intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_succession_dashboard' }) },
    { value: 'valuation_chat_agent', label: intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_valuation_specialist' }) },
    { value: 'continuity_guardian_agent', label: intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_continuity_specialist' }) },
    { value: 'content_creation_copilot_agent', label: intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_content_copilot' }) }
  ];

  return (
    <div className="space-y-6">
      {/* Save Status */}
      {saveStatus && (
        <div className="bg-white rounded-lg border border-succession-border p-3 shadow-sm">
          <div className="flex items-center gap-2 text-sm">
            {saveStatus === 'saving' ? (
              <>
                <ArrowPathIcon className="h-4 w-4 text-succession-text-secondary animate-spin" />
                <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.saving' })}</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-medium">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.settings_saved' })}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Appearance */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <PaintBrushIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.appearance' })}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.appearance_description' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Theme */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.theme' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.theme_description' })}</p>
            </div>
            <select
              value={settings.theme}
              onChange={(e) => updateSetting('theme', e.target.value)}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value="light">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.theme_light' })}</option>
              <option value="dark">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.theme_dark' })}</option>
              <option value="auto">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.theme_auto' })}</option>
            </select>
          </div>

          {/* Compact Mode */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.compact_mode' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.compact_mode_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('compactMode', !settings.compactMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.compactMode ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.compactMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
              <BellIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.notifications' })}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.notifications_description' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Desktop Notifications */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.desktop_notifications' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.desktop_notifications_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('desktopNotifications', !settings.desktopNotifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.desktopNotifications ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.desktopNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Sound */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.sound_effects' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.sound_effects_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('soundEnabled', !settings.soundEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.soundEnabled ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.soundEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Unread Badge */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.unread_badge' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.unread_badge_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('unreadBadge', !settings.unreadBadge)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.unreadBadge ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.unreadBadge ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Agent Preferences */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center">
              <SparklesIcon className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_preferences' })}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_preferences_description' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Default Agent */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.default_agent' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.default_agent_description' })}</p>
            </div>
            <select
              value={settings.defaultAgent}
              onChange={(e) => updateSetting('defaultAgent', e.target.value)}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              {agentOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Auto Switch */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.auto_switch_agents' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.auto_switch_agents_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('autoSwitchEnabled', !settings.autoSwitchEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.autoSwitchEnabled ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.autoSwitchEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Agent Suggestions */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_suggestions' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.agent_suggestions_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('showAgentSuggestions', !settings.showAgentSuggestions)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.showAgentSuggestions ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.showAgentSuggestions ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Conversation Management */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <ArrowPathIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.conversation_management' })}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.conversation_management_description' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Retention Days */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_period' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_period_description' })}</p>
            </div>
            <select
              value={settings.retentionDays}
              onChange={(e) => updateSetting('retentionDays', parseInt(e.target.value))}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value={7}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_7_days' })}</option>
              <option value={30}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_30_days' })}</option>
              <option value={90}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_90_days' })}</option>
              <option value={365}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_1_year' })}</option>
              <option value={-1}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.retention_forever' })}</option>
            </select>
          </div>

          {/* Auto Delete */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.auto_delete' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.auto_delete_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('autoDeleteOld', !settings.autoDeleteOld)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.autoDeleteOld ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.autoDeleteOld ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Max Conversations */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.max_conversations' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.max_conversations_description' })}</p>
            </div>
            <select
              value={settings.maxConversations}
              onChange={(e) => updateSetting('maxConversations', parseInt(e.target.value))}
              className="px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={-1}>{intl.formatMessage({ id: 'succession.agent_system.settings_panel.max_conversations_unlimited' })}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <ShieldCheckIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.privacy' })}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.agent_system.settings_panel.privacy_description' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Save History */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.save_history' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.save_history_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('saveHistory', !settings.saveHistory)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.saveHistory ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.saveHistory ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Share Analytics */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.share_analytics' })}</label>
              <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.settings_panel.share_analytics_description' })}</p>
            </div>
            <button
              onClick={() => updateSetting('shareAnalytics', !settings.shareAnalytics)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.shareAnalytics ? 'bg-succession-primary' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.shareAnalytics ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 border-b border-succession-border">
          <h2 className="text-lg font-semibold text-succession-text-primary">
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.data_management' })}
          </h2>
          <p className="text-sm text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.data_management_description' })}
          </p>
        </div>

        <div className="p-6 space-y-3">
          {/* Export */}
          <button
            onClick={handleExportSettings}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-succession-border bg-white text-succession-text-primary rounded-lg hover:bg-succession-bg-secondary transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.export_settings' })}
          </button>

          {/* Import */}
          <label className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-succession-border bg-white text-succession-text-primary rounded-lg hover:bg-succession-bg-secondary transition-colors cursor-pointer">
            <DocumentArrowUpIcon className="h-5 w-5" />
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.import_settings' })}
            <input
              type="file"
              accept=".json"
              onChange={handleImportSettings}
              className="hidden"
            />
          </label>

          {/* Clear History */}
          <button
            onClick={handleClearHistory}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-red-300 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            <TrashIcon className="h-5 w-5" />
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.clear_all_history' })}
          </button>

          {/* Reset */}
          <button
            onClick={handleResetSettings}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-red-300 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5" />
            {intl.formatMessage({ id: 'succession.agent_system.settings_panel.reset_to_defaults' })}
          </button>
        </div>
      </div>

      {/* Info Footer */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> {intl.formatMessage({ id: 'succession.agent_system.settings_panel.browser_storage_note' })}
        </p>
      </div>
    </div>
  );
};

SettingsPanel.propTypes = {
  user: PropTypes.object,
  company: PropTypes.object
};

export default SettingsPanel;
